import { Helmet } from 'react-helmet';
import { useMemo } from 'react';
import whitepapers from '../data/whitepapers';

const WhitepaperDetail = ({ slug, onNavigate }) => {
  const whitepaper = useMemo(() => {
    return whitepapers.find((entry) => entry.slug === slug) || whitepapers[0];
  }, [slug]);

  const pdfLink =
    typeof window !== 'undefined'
      ? `${window.location.origin}${whitepaper.pdfUrl}`
      : whitepaper.pdfUrl;

  const apiStyles = [
    {
      aspect: 'Protocol',
      soap: 'A strict, standardized protocol that can operate over multiple transports, including HTTP and SMTP.',
      rest: 'An architectural style that leverages HTTP methods (GET, POST, PUT, DELETE) for communication.',
    },
    {
      aspect: 'State',
      soap: 'Can be stateful, maintaining client session data between requests for enterprise workflows.',
      rest: 'Typically stateless, requiring each request to carry the full context for processing.',
    },
    {
      aspect: 'Message format',
      soap: 'Uses XML inside a rigid SOAP envelope with strict schema requirements.',
      rest: 'Commonly uses JSON, but can support XML, HTML, and other formats.',
    },
    {
      aspect: 'Error handling',
      soap: 'Standardized error reporting via the SOAP fault element.',
      rest: 'Relies on HTTP status codes (404, 500, 401) for success or failure.',
    },
    {
      aspect: 'Complexity',
      soap: 'More heavyweight due to verbose XML and strict protocol requirements.',
      rest: 'Simpler, lightweight, and preferred for modern web and mobile services.',
    },
    {
      aspect: 'Best use cases',
      soap: 'Banking, large enterprise integrations, and legacy systems needing WS-Security.',
      rest: 'Public APIs, mobile apps, and microservices-based architectures.',
    },
  ];

  const authenticationMethods = [
    'Basic authentication using Base64 encoded credentials secured by TLS.',
    'API keys passed in headers or query parameters for application identification.',
    'Bearer tokens that grant access based on possession of the token.',
    'OAuth 2.0 for delegated access across multiple grant types.',
    'JSON Web Tokens (JWT) for compact, self-contained session tokens.',
    'OpenID Connect for identity verification layered on OAuth 2.0.',
  ];

  const owaspRisks = [
    {
      title: 'Broken Object Level Authorization (BOLA)',
      threat:
        'Endpoints fail to verify that a user can access a specific object. Attackers swap IDs to access other users\' data.',
      mitigation:
        'Enforce server-side authorization checks and use non-guessable identifiers such as UUIDs.',
    },
    {
      title: 'Broken Authentication',
      threat:
        'Weak password policies, insecure token handling, or poor session invalidation allow session hijacking.',
      mitigation:
        'Use strong password requirements, short-lived tokens, and multi-factor authentication for sensitive actions.',
    },
    {
      title: 'Broken Object Property Level Authorization',
      threat:
        'APIs allow users to read or modify object properties they should not access, enabling mass assignment.',
      mitigation:
        'Apply strict schema validation and field whitelisting with DTOs to block property injection.',
    },
    {
      title: 'Unrestricted Resource Consumption',
      threat:
        'Lack of limits on payload size or request volume leads to denial-of-service conditions.',
      mitigation:
        'Implement rate limiting, request throttling, size limits, and concurrency controls at the gateway.',
    },
    {
      title: 'Broken Function Level Authorization',
      threat:
        'Sensitive functions are exposed to users without proper role checks, enabling privilege escalation.',
      mitigation:
        'Validate roles and permissions at every endpoint and map user roles to allowed functions.',
    },
    {
      title: 'Unrestricted Access to Sensitive Business Flows',
      threat:
        'High-impact workflows such as bulk exports or account deletions can be abused at scale.',
      mitigation:
        'Flag critical flows, add extra verification steps, and apply dedicated monitoring and rate limits.',
    },
    {
      title: 'Server Side Request Forgery (SSRF)',
      threat:
        'User-supplied URLs can trigger requests to internal services, exposing internal assets.',
      mitigation:
        'Whitelist approved domains, block internal address ranges, and segment internal resources.',
    },
    {
      title: 'Security Misconfiguration',
      threat:
        'Insecure defaults, exposed debug endpoints, or unpatched systems create exploitable entry points.',
      mitigation:
        'Harden configurations, remove unused services, and enforce least privilege across the stack.',
    },
    {
      title: 'Improper Inventory Management',
      threat:
        'Undocumented or deprecated APIs remain exposed, creating shadow attack surfaces.',
      mitigation:
        'Maintain a complete API inventory, enforce lifecycle policies, and restrict non-production access.',
    },
    {
      title: 'Unsafe Consumption of APIs',
      threat:
        'Implicit trust in third-party data can introduce injection or code execution vulnerabilities.',
      mitigation:
        'Treat all external data as untrusted and validate every response before processing.',
    },
  ];

  const documentationChecklist = [
    'Endpoints and supported HTTP methods.',
    'Required and optional parameters with clear descriptions.',
    'Request and response examples including status codes.',
    'Error handling guidance with every possible error message.',
    'Authentication and authorization workflows.',
  ];

  const pentestDeliverables = [
    'Executive summary that communicates risk to non-technical stakeholders.',
    'Methodology, scope, and tools used during testing.',
    'Findings with severity, evidence, and business impact.',
    'Actionable remediation recommendations.',
    'Appendices containing logs, payloads, and supporting data.',
  ];

  const fieldGuideStages = [
    {
      title: 'Stage 1: Mastering the Terrain',
      summary:
        'Build fluency in API architecture, authentication flows, and documentation to map the attack surface with confidence.',
    },
    {
      title: 'Stage 2: Assembling the Toolkit',
      summary:
        'Blend command-line precision, GUI exploration, and scripting automation to validate security controls at speed.',
    },
    {
      title: 'Stage 3: Modern Attacker Playbook',
      summary:
        'Use OWASP API Security Top 10 as a field map for authorization flaws, resource abuse, and trust failures.',
    },
    {
      title: 'Stage 4: Professional Engagement',
      summary:
        'Translate technical findings into business outcomes through structured test plans, reports, and debriefs.',
    },
  ];

  const toolkitItems = [
    {
      name: 'cURL',
      description:
        'Command-line control for crafting precise requests, replaying traffic, and validating edge cases quickly.',
      advantage: 'Ideal for scripting, quick tests, and reproducible proofs of concept.',
    },
    {
      name: 'Postman',
      description:
        'A visual workspace for building collections, sharing requests, and collaborating on API test suites.',
      advantage: 'Simplifies exploration with built-in auth helpers, environments, and documentation.',
    },
    {
      name: 'SOAP UI',
      description:
        'Purpose-built tooling for SOAP and REST testing with strong support for enterprise workflows.',
      advantage: 'Excels at data-driven testing, service mocking, and regression suites.',
    },
    {
      name: 'Python + Requests',
      description:
        'The automation layer for chaining calls, fuzzing inputs, and integrating tests into CI pipelines.',
      advantage: 'Unmatched flexibility for large-scale testing and repeatable automation.',
    },
  ];

  const testPlanElements = [
    'Objective and success criteria for the engagement.',
    'Scope definition with clear in-scope and out-of-scope boundaries.',
    'Methodology selection (grey-box, OWASP WSTG, custom playbooks).',
    'Execution plan with timelines, tooling, and escalation paths.',
    'Reporting structure that aligns with stakeholder expectations.',
  ];

  const debriefParticipants = [
    'Pentest team',
    'Security team',
    'Development team',
    'Business stakeholders',
  ];

  const toc = [
    { id: 'executive-summary', label: '0. Executive Summary' },
    { id: 'foundations', label: '1.0 Foundations of Modern APIs' },
    { id: 'access-control', label: '2.0 Authentication and Authorization' },
    { id: 'owasp-top-10', label: '3.0 OWASP API Security Top 10' },
    { id: 'field-guide', label: '4.0 Practitioner Field Guide' },
    { id: 'toolkit', label: '5.0 Operator Toolkit' },
    { id: 'engagement', label: '6.0 Professional Engagement' },
    { id: 'proactive-defense', label: '7.0 Proactive Defense Framework' },
    { id: 'conclusion', label: '8.0 Conclusion' },
  ];

  const handleNavigate = (path) => {
    if (onNavigate) {
      onNavigate(path);
      return;
    }
    window.location.assign(path);
  };

  return (
    <div className="whitepaper-surface">
      <Helmet>
        <title>{whitepaper.title} | r4j3sh</title>
        <meta name="description" content={whitepaper.summary} />
      </Helmet>
      <div className="whitepaper-grid">
        <section className="pt-28 pb-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] items-center">
              <div className="space-y-6">
                <button
                  className="whitepaper-button-secondary px-4 py-2 rounded-full text-xs font-semibold"
                  onClick={() => handleNavigate('/whitepapers')}
                >
                  Back to Whitepapers
                </button>
                <p className="whitepaper-eyebrow text-slate-500">Whitepaper</p>
                <h1 className="whitepaper-display text-4xl sm:text-5xl lg:text-6xl leading-tight">
                  {whitepaper.title}
                </h1>
                <p className="text-lg text-slate-600 max-w-2xl">{whitepaper.summary}</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="whitepaper-pill px-3 py-1 rounded-full">{whitepaper.category}</span>
                  <span className="whitepaper-pill px-3 py-1 rounded-full">{whitepaper.dateLabel}</span>
                  <span className="whitepaper-pill px-3 py-1 rounded-full">{whitepaper.readingTime}</span>
                </div>
              </div>
              <div className="whitepaper-card rounded-3xl p-5">
                <img
                  src={whitepaper.coverImage}
                  alt={whitepaper.title}
                  className="w-full h-72 object-cover rounded-2xl"
                />
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-semibold text-slate-800">Inside the paper</p>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li>Modern API architecture patterns and access control foundations.</li>
                    <li>OWASP API Security Top 10 threat analysis and mitigation strategies.</li>
                    <li>Operator toolkits, professional engagement workflows, and layered defenses.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-[260px_1fr]">
            <aside className="space-y-6 lg:sticky lg:top-24 h-fit print-hide">
              <div className="whitepaper-card rounded-3xl p-5">
                <p className="text-sm font-semibold text-slate-800 mb-3">In this paper</p>
                <nav className="space-y-2 text-sm text-slate-600">
                  {toc.map((item) => (
                    <a key={item.id} href={`#${item.id}`} className="block hover:text-slate-900">
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>

              <div id="download" className="whitepaper-card rounded-3xl p-5 space-y-4">
                <p className="text-sm font-semibold text-slate-800">Whitepaper PDF</p>
                <p className="text-sm text-slate-600">
                  Share this direct link by email or click below to download the PDF.
                </p>
                <a
                  className="whitepaper-button w-full inline-flex items-center justify-center py-2.5 rounded-full text-sm font-semibold"
                  href={whitepaper.pdfUrl}
                  download
                >
                  Download PDF
                </a>
                <p className="text-xs text-slate-500 break-all">
                  {pdfLink}
                </p>
                <p className="text-xs text-slate-500">
                  Copyright (c) 2025 Rajesh Mondal. All rights reserved. Personal use only.
                </p>
              </div>
            </aside>

            <article className="space-y-12 text-base leading-relaxed">
              <section id="executive-summary" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">0. Executive Summary</p>
                  <h2 className="text-3xl font-semibold">
                    Strategic API Security for the Modern Threat Landscape
                  </h2>
                </div>
                <p className="text-slate-600">
                  APIs now power everything from mobile experiences to internal service meshes, making them
                  a dominant source of risk and opportunity. This whitepaper combines strategic analysis with
                  a practitioner field guide to help security leaders, engineers, and auditors align on
                  priority defenses.
                </p>
                <p className="text-slate-600">
                  The document blends two perspectives: a threat landscape assessment grounded in OWASP API
                  Security Top 10 and a field guide for ethical hackers that details the tooling, workflows,
                  and engagement practices needed to validate controls in real-world environments.
                </p>
                <div className="whitepaper-card rounded-3xl p-5">
                  <p className="text-sm text-slate-600">
                    Outcome: a unified playbook that connects architecture, controls, and testing discipline
                    into a resilient API security posture.
                  </p>
                </div>
              </section>

              <section id="foundations" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">1.0 Foundations</p>
                  <h2 className="text-3xl font-semibold">
                    The Foundational Role of APIs in Modern Architectures
                  </h2>
                </div>
                <p className="text-slate-600">
                  Application Programming Interfaces (APIs) are the connective tissue of modern software,
                  enabling modular architectures, seamless integrations, and rich digital experiences.
                  Understanding core API principles, from architectural styles to deployment models, is the
                  essential first step toward securing them.
                </p>
                <p className="text-slate-600">
                  A web API is a contract that defines how one software component requests data or services
                  from another over HTTP. It allows mobile apps to fetch profiles, third-party services to
                  access data, and internal microservices to exchange information efficiently.
                </p>
                <p className="text-slate-600">
                  Two foundational styles dominate the web API landscape: SOAP and REST.
                </p>
                <div className="overflow-x-auto whitepaper-card rounded-3xl overflow-hidden">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-white/80">
                      <tr>
                        <th className="px-4 py-3 font-semibold text-slate-700">Aspect</th>
                        <th className="px-4 py-3 font-semibold text-slate-700">SOAP</th>
                        <th className="px-4 py-3 font-semibold text-slate-700">REST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiStyles.map((row) => (
                        <tr key={row.aspect} className="border-t border-slate-200/60">
                          <td className="px-4 py-3 font-semibold text-slate-700">{row.aspect}</td>
                          <td className="px-4 py-3 text-slate-600">{row.soap}</td>
                          <td className="px-4 py-3 text-slate-600">{row.rest}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-slate-600">
                  This distinction mirrors the architectural shift from monolithic systems to microservices.
                  While microservices improve scalability, the proliferation of inter-service APIs increases
                  the attack surface for issues like SSRF and unsafe consumption. Inventory management and
                  zero-trust principles become non-negotiable.
                </p>
                <p className="text-slate-600">
                  With foundational concepts established, the primary security challenge becomes controlling
                  who can access these powerful and distributed interfaces.
                </p>
              </section>

              <section id="access-control" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">2.0 Access Control</p>
                  <h2 className="text-3xl font-semibold">
                    Securing API Access: Authentication and Authorization Paradigms
                  </h2>
                </div>
                <p className="text-slate-600">
                  Treating authentication and authorization as distinct, mandatory controls is the cornerstone
                  of a defensible API security posture. Authentication verifies identity; authorization validates
                  permissions for specific resources and actions.
                </p>
                <div className="whitepaper-card rounded-3xl p-5">
                  <p className="text-sm font-semibold text-slate-800 mb-3">Common authentication methods</p>
                  <ul className="grid gap-2 text-sm text-slate-600">
                    {authenticationMethods.map((method) => (
                      <li key={method}>- {method}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-slate-600">
                  Once a user is authenticated, permissions must be managed through models such as Role-Based
                  Access Control (RBAC) and Attribute-Based Access Control (ABAC). RBAC assigns permissions to
                  predefined roles, while ABAC uses dynamic attributes like user, resource, or environment context
                  for fine-grained decisions.
                </p>
                <p className="text-slate-600">
                  Rate limiting is a critical complementary control, preventing abusive usage patterns and
                  mitigating brute-force or denial-of-service attacks.
                </p>
              </section>

              <section id="owasp-top-10" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">3.0 Threat Landscape</p>
                  <h2 className="text-3xl font-semibold">
                    Dissecting the API Threat Landscape: OWASP API Security Top 10 (2023)
                  </h2>
                </div>
                <p className="text-slate-600">
                  The OWASP API Security Top 10 identifies the most critical security risks facing modern APIs.
                  Each category below pairs threat analysis with mitigation strategy to guide prioritization.
                </p>
                <div className="grid gap-4">
                  {owaspRisks.map((risk, index) => (
                    <div key={risk.title} className="whitepaper-card rounded-3xl p-5">
                      <h3 className="text-lg font-semibold text-slate-800">
                        {index + 1}. {risk.title}
                      </h3>
                      <p className="text-sm text-slate-600 mt-2">
                        <span className="font-semibold text-slate-700">Threat analysis:</span> {risk.threat}
                      </p>
                      <p className="text-sm text-slate-600 mt-2">
                        <span className="font-semibold text-slate-700">Mitigation strategy:</span> {risk.mitigation}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-slate-600">
                  The evolution from the 2019 to the 2023 OWASP list reflects a maturing understanding of API
                  risk. New categories such as SSRF and unsafe consumption highlight the growing risks of
                  interconnected microservices.
                </p>
              </section>

              <section id="field-guide" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">4.0 Practitioner Field Guide</p>
                  <h2 className="text-3xl font-semibold">
                    The Ethical Hacker's Journey Through API Security
                  </h2>
                </div>
                <p className="text-slate-600">
                  Practical testing requires a progression: learn the terrain, assemble the right tools, apply
                  the modern attacker playbook, and deliver findings in a professional engagement. These stages
                  transform theory into repeatable security outcomes.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {fieldGuideStages.map((stage) => (
                    <div key={stage.title} className="whitepaper-card rounded-3xl p-5">
                      <h3 className="text-lg font-semibold text-slate-800">{stage.title}</h3>
                      <p className="text-sm text-slate-600 mt-2">{stage.summary}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="toolkit" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">5.0 Operator Toolkit</p>
                  <h2 className="text-3xl font-semibold">
                    Tools and techniques that scale API security testing
                  </h2>
                </div>
                <p className="text-slate-600">
                  Modern API security work blends precision tooling with automation. The following tools anchor
                  reliable discovery, testing, and validation workflows across REST and SOAP environments.
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {toolkitItems.map((tool) => (
                    <div key={tool.name} className="whitepaper-card rounded-3xl p-5">
                      <h3 className="text-lg font-semibold text-slate-800">{tool.name}</h3>
                      <p className="text-sm text-slate-600 mt-2">{tool.description}</p>
                      <p className="text-sm text-slate-600 mt-2">
                        <span className="font-semibold text-slate-700">Key advantage:</span> {tool.advantage}
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              <section id="engagement" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">6.0 Professional Engagement</p>
                  <h2 className="text-3xl font-semibold">
                    Planning, reporting, and debriefing with precision
                  </h2>
                </div>
                <p className="text-slate-600">
                  A professional engagement translates technical insight into business action. Structured
                  planning and clear reporting are essential for sustainable remediation.
                </p>
                <div className="grid gap-4 lg:grid-cols-3">
                  <div className="whitepaper-card rounded-3xl p-5">
                    <h3 className="text-lg font-semibold text-slate-800">Test plan</h3>
                    <ul className="text-sm text-slate-600 space-y-2 mt-3">
                      {testPlanElements.map((item) => (
                        <li key={item}>- {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="whitepaper-card rounded-3xl p-5">
                    <h3 className="text-lg font-semibold text-slate-800">Test report</h3>
                    <ul className="text-sm text-slate-600 space-y-2 mt-3">
                      {pentestDeliverables.map((item) => (
                        <li key={item}>- {item}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="whitepaper-card rounded-3xl p-5">
                    <h3 className="text-lg font-semibold text-slate-800">Debrief meeting</h3>
                    <p className="text-sm text-slate-600 mt-2">
                      Align on remediation strategy, prioritization, and ownership.
                    </p>
                    <ul className="text-sm text-slate-600 space-y-2 mt-3">
                      {debriefParticipants.map((item) => (
                        <li key={item}>- {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>

              <section id="proactive-defense" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">7.0 Defense Strategy</p>
                  <h2 className="text-3xl font-semibold">
                    Proactive Defense: Architectural and Procedural Safeguards
                  </h2>
                </div>
                <p className="text-slate-600">
                  A resilient security posture demands proactive, layered defenses that combine architectural
                  safeguards, procedural rigor, and secure documentation. These elements anticipate threats
                  before they are exploited.
                </p>
                <div className="whitepaper-card rounded-3xl p-5 space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">
                    Architectural defenses: API gateways and firewalls
                  </h3>
                  <p className="text-sm text-slate-600">
                    An API gateway centralizes authentication, rate limiting, and logging, acting as a countermeasure
                    to resource exhaustion. An API firewall sits in front of services to inspect traffic, enforce
                    schema validation, and block malicious payloads.
                  </p>
                </div>
                <div className="whitepaper-card rounded-3xl p-5 space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">
                    Secure documentation as a strategic asset
                  </h3>
                  <p className="text-sm text-slate-600">
                    Documentation reduces misconfiguration risk and enables secure integrations, but it must balance
                    developer clarity with the need to avoid revealing sensitive implementation details.
                  </p>
                  <ul className="text-sm text-slate-600 space-y-2">
                    {documentationChecklist.map((item) => (
                      <li key={item}>- {item}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-slate-600">
                  Operational maturity grows when security teams combine these safeguards with continuous testing
                  and disciplined remediation cycles.
                </p>
              </section>

              <section id="conclusion" className="space-y-6">
                <div>
                  <p className="whitepaper-eyebrow text-slate-400">8.0 Conclusion</p>
                  <h2 className="text-3xl font-semibold">
                    Establishing a Resilient API Security Posture
                  </h2>
                </div>
                <p className="text-slate-600">
                  As APIs become more central to modern architectures, they expand the organizational attack surface.
                  A resilient posture requires defense in depth that combines strong access control, modern threat
                  intelligence, and proactive operational safeguards.
                </p>
                <p className="text-slate-600">
                  Security is not a one-time achievement. It is a continuous cycle of monitoring, testing, and
                  adaptation to evolving technologies and attacker techniques. Organizations that commit to this
                  cycle can keep their defenses aligned with the threats of tomorrow.
                </p>
                <div className="whitepaper-card rounded-3xl p-5">
                  <p className="text-sm text-slate-600">
                    For tailored guidance, reach out directly to schedule a security briefing or review.
                  </p>
                  <button
                    className="whitepaper-button mt-4 px-6 py-2.5 rounded-full text-sm font-semibold"
                    onClick={() => handleNavigate('/#contact')}
                  >
                    Book a briefing
                  </button>
                </div>
              </section>
            </article>
          </div>
        </section>
      </div>
    </div>
  );
};

export default WhitepaperDetail;
